import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api, { batchPaymentApi } from '../../utils/api';
import { format, parseISO } from 'date-fns';
import { useSnackbar } from 'notistack';
import { formatDollars } from '../../utils/money';
import { filterRecordablePaymentTypes } from '../../utils/paymentTypes';
import { todayLocalISO, ymdToNoonUtcIso } from '../../utils/dateUtils';
import { useRBAC } from '../../auth/useRBAC';
import { invoicingControls } from '../../auth/pageControls';

// ── Types ────────────────────────────────────────────────────────────────────

interface OpenInvoice {
  id: string;
  invoiceNumber: string;
  issuedAt?: string;
  serviceDate?: string;
  client?: { id: string; name: string };
  /** `ownerName` is the SERVER-resolved bill-to (billing/bill-to.ts), not the patient's owner. */
  horse?: { id: string; name: string; ownerName?: string; owners?: any[] };
  /** One-time payer snapshot stored on the invoice instead of a clientId (INV-13). */
  thirdPartyPayer?: { name?: string; email?: string; phone?: string; address?: string } | null;
  amount: number;
  taxAmount?: number;
  balanceDue?: number;
  lineItems?: Array<{ description?: string }>;
}

interface PaymentType {
  value: string;
  label: string;
}

interface BatchResult {
  batchId: string;
  count: number;
  totalApplied: number;
  results: Array<{ invoiceId: string; paymentId: string; balanceDue?: number }>;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const STEPS = ['Search & Select', 'Confirm'];

// ── Helpers ───────────────────────────────────────────────────────────────────

function invoiceDate(inv: OpenInvoice): string {
  const raw = inv.serviceDate || inv.issuedAt;
  if (!raw) return '—';
  try {
    return format(parseISO(raw), 'MMM d, yyyy');
  } catch {
    return raw;
  }
}

function invoiceBalance(inv: OpenInvoice): number {
  if (typeof inv.balanceDue === 'number') return inv.balanceDue;
  return (inv.amount ?? 0) + (inv.taxAmount ?? 0);
}

function primaryService(inv: OpenInvoice): string {
  const items = inv.lineItems;
  if (items && items.length > 0) return items[0].description || '—';
  return '—';
}

/**
 * WHO this invoice bills — the same precedence the server resolves once
 * (`backend/src/modules/billing/bill-to.ts`): the direct client, then the third-party
 * payer snapshot, then the server-resolved `horse.ownerName`, and only then the patient's
 * ownership JSON. Without the payer legs this screen labelled an insurer-billed row with
 * the HORSE OWNER's name, and a biller recording a cheque read the wrong payer on the
 * confirm step, the table and the exported CSV.
 */
function clientName(inv: OpenInvoice): string {
  if (inv.client?.name) return inv.client.name;
  if (inv.thirdPartyPayer?.name) return inv.thirdPartyPayer.name;
  if (inv.horse?.ownerName) return inv.horse.ownerName;
  const owners = inv.horse?.owners;
  if (Array.isArray(owners) && owners.length > 0) return owners[0].name || '—';
  return '—';
}

// ── Main component ─────────────────────────────────────────────────────────────

const BulkMarkPaid: FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  // ⚠ manage:PAYMENTS, not manage:charges (INV-108). "Mark as Paid" reads like an invoice status
  // edit and is not one: this screen posts `/payments/record-batch` (payments.controller.ts
  // `recordBatch` :49-50), and the status route it sounds like has no web caller. Gating it on
  // the invoice slug would have hidden the bulk-payment screen from the front desk it exists for.
  const rbac = useRBAC();
  const can = invoicingControls(rbac);

  // ── Screen state ──
  const [step, setStep] = useState(0); // 0 = select, 1 = confirm, 2 = detail

  // ── Screen 1 state ──
  const [search, setSearch] = useState('');
  const [paymentType, setPaymentType] = useState('');
  const [dateReceived, setDateReceived] = useState(
    () => todayLocalISO(),
  );
  const [batchNote, setBatchNote] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [refs, setRefs] = useState<Record<string, string>>({}); // invoiceId → ref #

  // ── Screen 2 state ──
  const [batchResult, setBatchResult] = useState<BatchResult | null>(null);
  // Snapshot of the invoices that were just paid: the success invalidations
  // refetch the open-invoice list, which drops them from allInvoices — the
  // Confirm/Detail screens must keep rendering from this frozen copy.
  const [confirmedInvoices, setConfirmedInvoices] = useState<OpenInvoice[]>([]);

  // ── Screen 3 state ──
  const [detailInvoiceId, setDetailInvoiceId] = useState<string | null>(null);
  const [detailRef, setDetailRef] = useState('');
  const [originalDetailRef, setOriginalDetailRef] = useState('');
  const [refSaving, setRefSaving] = useState(false);

  // ── Data fetching ──
  const { data: invoicesData, isLoading: invoicesLoading } = useQuery(
    ['bulk-mark-paid-invoices'],
    () =>
      api
        .get('/billing', { params: { page: 1, limit: 200 } })
        .then((res) => {
          const list = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
          return list.filter((i: any) =>
            ['pending', 'overdue', 'partially_paid'].includes(i.status),
          ) as OpenInvoice[];
        }),
    { staleTime: 30_000 },
  );

  const { data: paymentTypes } = useQuery<PaymentType[]>(
    ['payment-types'],
    () => api.get('/payment-types').then((res) => res.data),
    { staleTime: 60_000 },
  );

  // Bulk recording offers every active Settings method — including card types,
  // for cards charged on an external terminal. Nothing here ever charges; the
  // caption under the chips says so. Only the Stripe payment-link flow and the
  // reserved account-credit value stay excluded (utils/paymentTypes.ts).
  const bulkPaymentTypes = useMemo(
    () => filterRecordablePaymentTypes(paymentTypes ?? []),
    [paymentTypes],
  );

  // Keep the selected method valid against the live list: default it once
  // types load, and snap it back to the first available type if Settings
  // deactivates the one currently selected.
  useEffect(() => {
    if (bulkPaymentTypes.length === 0) {
      if (paymentType) setPaymentType('');
      return;
    }
    if (!paymentType || !bulkPaymentTypes.some((t) => t.value === paymentType)) {
      setPaymentType(bulkPaymentTypes[0].value);
    }
  }, [bulkPaymentTypes, paymentType]);

  const allInvoices: OpenInvoice[] = invoicesData ?? [];

  // ── Filtered list ──
  const visible = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return allInvoices;
    return allInvoices.filter(
      (inv) =>
        inv.invoiceNumber?.toLowerCase().includes(q) ||
        clientName(inv).toLowerCase().includes(q) ||
        inv.horse?.name?.toLowerCase().includes(q),
    );
  }, [allInvoices, search]);

  // ── Selection helpers ──
  const selectedInvoices = useMemo(
    () => allInvoices.filter((inv) => selected.has(inv.id)),
    [allInvoices, selected],
  );

  const selectionTotal = useMemo(
    () => selectedInvoices.reduce((s, inv) => s + invoiceBalance(inv), 0),
    [selectedInvoices],
  );

  const allVisibleSelected =
    visible.length > 0 && visible.every((inv) => selected.has(inv.id));
  const someVisibleSelected = visible.some((inv) => selected.has(inv.id));

  function toggleInvoice(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visible.forEach((inv) => next.delete(inv.id));
      } else {
        visible.forEach((inv) => next.add(inv.id));
      }
      return next;
    });
  }

  // ── Batch submit ──
  const recordMutation = useMutation(
    () =>
      batchPaymentApi.record({
        paymentType,
        // Same anchor as every other payment-date post (DATE-KINDS §2.5 W6): the
        // picker is `type="date"`, so this is a bare `yyyy-MM-dd` and the server
        // used to store it at UTC midnight, i.e. the PREVIOUS day for every US
        // clinic that reads it back.
        paymentDate: ymdToNoonUtcIso(dateReceived) || undefined,
        referenceNote: batchNote.trim() || undefined,
        allocations: selectedInvoices.map((inv) => ({
          invoiceId: inv.id,
          amount: invoiceBalance(inv),
          referenceNote: refs[inv.id]?.trim() || undefined,
        })),
      }),
    {
      onSuccess: (result) => {
        setConfirmedInvoices(selectedInvoices);
        setBatchResult(result);
        setStep(1);
        queryClient.invalidateQueries(['bulk-mark-paid-invoices']);
        queryClient.invalidateQueries(['invoices']);
        queryClient.invalidateQueries(['billing-stats']);
        queryClient.invalidateQueries(['open-invoices']);
      },
      onError: (err: any) => {
        enqueueSnackbar(
          err?.response?.data?.message ?? 'Failed to record payments',
          { variant: 'error' },
        );
      },
    },
  );

  // ── Screen 3: open detail for an invoice ──
  function openDetail(invoiceId: string) {
    setDetailInvoiceId(invoiceId);
    const ref = refs[invoiceId] ?? '';
    setDetailRef(ref);
    setOriginalDetailRef(ref);
    setStep(2);
  }

  async function saveDetailRef() {
    if (!detailInvoiceId || !batchResult) return;
    const paymentId = batchResult.results.find(
      (r) => r.invoiceId === detailInvoiceId,
    )?.paymentId;
    if (!paymentId) return;
    setRefSaving(true);
    try {
      await batchPaymentApi.updateReference(paymentId, detailRef.trim() || null);
      setRefs((prev) => ({ ...prev, [detailInvoiceId]: detailRef.trim() }));
      setOriginalDetailRef(detailRef.trim());
      enqueueSnackbar('Reference number saved', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to save reference number', { variant: 'error' });
    } finally {
      setRefSaving(false);
    }
  }

  // ── Reset ──
  function startOver() {
    setStep(0);
    setSelected(new Set());
    setRefs({});
    setSearch('');
    setBatchNote('');
    setBatchResult(null);
    setConfirmedInvoices([]);
    setDetailInvoiceId(null);
  }

  // ── Date display ──
  const dateDisplay = useMemo(() => {
    if (!dateReceived) return 'Today';
    try {
      return format(parseISO(dateReceived), 'MMMM d, yyyy');
    } catch {
      return dateReceived;
    }
  }, [dateReceived]);

  const paymentTypeLabel = useMemo(
    () => paymentTypes?.find((t) => t.value === paymentType)?.label ?? paymentType,
    [paymentTypes, paymentType],
  );

  // ── Shared styles ──
  const cardSx = {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 2,
    overflow: 'hidden',
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // SCREEN 1: Search & Select
  // ─────────────────────────────────────────────────────────────────────────────
  const renderScreen1 = () => (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={0.5}>
        Bulk Mark as Paid
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2.5}>
        Select open invoices to mark paid with a shared payment method.
      </Typography>

      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search by invoice #, client, or horse…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      <Paper sx={cardSx}>
        {/* Method / date / note bar */}
        <Box
          sx={{
            px: 2,
            py: 1.25,
            bgcolor: 'grey.50',
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Method
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap">
            {bulkPaymentTypes.map((t) => (
              <Chip
                key={t.value}
                label={t.label}
                size="small"
                onClick={() => setPaymentType(t.value)}
                sx={{
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  cursor: 'pointer',
                  bgcolor: paymentType === t.value ? '#8B4513' : 'background.paper',
                  color: paymentType === t.value ? '#fff' : 'text.secondary',
                  border: '1.5px solid',
                  borderColor: paymentType === t.value ? '#8B4513' : 'divider',
                  '&:hover': { bgcolor: paymentType === t.value ? '#7a3c10' : 'grey.100' },
                }}
              />
            ))}
          </Stack>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Date received
          </Typography>
          <TextField
            type="date"
            size="small"
            value={dateReceived}
            onChange={(e) => setDateReceived(e.target.value)}
            sx={{ width: 150, '& input': { fontSize: '0.8rem', py: 0.625 } }}
          />

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Note
          </Typography>
          <TextField
            size="small"
            placeholder="e.g. Weekend wire batch"
            value={batchNote}
            onChange={(e) => setBatchNote(e.target.value)}
            sx={{ width: 200, '& input': { fontSize: '0.8rem', py: 0.625 } }}
          />

          {/* Full-width line so many method chips can't push it off-screen. */}
          <Typography variant="caption" color="text.secondary" sx={{ width: '100%', mt: -0.5 }}>
            Records a payment only — no card is charged.
          </Typography>
        </Box>

        {/* Table */}
        {invoicesLoading ? (
          <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : visible.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {allInvoices.length === 0
                ? 'No open invoices found'
                : 'No invoices match your search'}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell padding="checkbox" sx={{ pl: 2 }}>
                    <Checkbox
                      size="small"
                      checked={allVisibleSelected}
                      indeterminate={someVisibleSelected && !allVisibleSelected}
                      onChange={toggleAll}
                    />
                  </TableCell>
                  {['Invoice #', 'Date', 'Client', 'Horse', 'Balance', 'Ref # (optional)'].map(
                    (h) => (
                      <TableCell
                        key={h}
                        sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', whiteSpace: 'nowrap' }}
                      >
                        {h}
                      </TableCell>
                    ),
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map((inv) => {
                  const isSel = selected.has(inv.id);
                  return (
                    <TableRow
                      key={inv.id}
                      hover
                      selected={isSel}
                      onClick={() => toggleInvoice(inv.id)}
                      sx={{ cursor: 'pointer', '&.Mui-selected': { bgcolor: 'rgba(139,69,19,0.04)' } }}
                    >
                      <TableCell padding="checkbox" sx={{ pl: 2 }}>
                        <Checkbox
                          size="small"
                          checked={isSel}
                          onChange={() => toggleInvoice(inv.id)}
                          onClick={(e) => e.stopPropagation()}
                          sx={{ color: '#8B4513', '&.Mui-checked': { color: '#8B4513' } }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: isSel ? 'text.primary' : 'text.disabled' }}>
                        {inv.invoiceNumber}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {invoiceDate(inv)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', color: isSel ? 'text.primary' : 'text.secondary' }}>
                        {clientName(inv)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                        {inv.horse?.name ?? '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: isSel ? 700 : 400, color: isSel ? 'text.primary' : 'text.secondary', whiteSpace: 'nowrap' }}>
                        {formatDollars(invoiceBalance(inv))}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()} sx={{ minWidth: 130 }}>
                        <TextField
                          size="small"
                          placeholder="ref #"
                          disabled={!isSel}
                          value={refs[inv.id] ?? ''}
                          onChange={(e) =>
                            setRefs((prev) => ({ ...prev, [inv.id]: e.target.value }))
                          }
                          sx={{
                            width: '100%',
                            '& input': { fontSize: '0.75rem', py: 0.5 },
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': { borderColor: refs[inv.id] ? '#8B4513' : undefined },
                            },
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Footer */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {selected.size > 0 ? (
              <strong>{selected.size} invoice{selected.size > 1 ? 's' : ''} selected</strong>
            ) : (
              'No invoices selected'
            )}
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {selected.size > 0 && (
              <Typography variant="subtitle1" fontWeight={800} color="primary" sx={{ color: '#8B4513' }}>
                {formatDollars(selectionTotal)}
              </Typography>
            )}
            {can.recordPayment && (
              <Button
                variant="contained"
                disabled={selected.size === 0 || !paymentType || recordMutation.isLoading}
                onClick={() => recordMutation.mutate()}
                sx={{ bgcolor: '#8B4513', '&:hover': { bgcolor: '#7a3c10' }, textTransform: 'none', fontWeight: 600 }}
                startIcon={recordMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
              >
                Mark as Paid
              </Button>
            )}
          </Stack>
        </Box>
      </Paper>
    </Box>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // SCREEN 2: Confirm
  // ─────────────────────────────────────────────────────────────────────────────
  const renderScreen2 = () => {
    if (!batchResult) return null;
    return (
      <Box sx={{ maxWidth: 680, mx: 'auto' }}>
        <Typography variant="h5" fontWeight={700} mb={0.5}>
          Invoices Marked Paid
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2.5}>
          Payment recorded against each invoice individually.
        </Typography>

        {/* Success banner */}
        <Alert
          icon={<CheckCircleIcon />}
          severity="success"
          sx={{ mb: 2.5, borderRadius: 2, '& .MuiAlert-message': { width: '100%' } }}
        >
          <Typography fontWeight={700}>
            {batchResult.count} invoice{batchResult.count > 1 ? 's' : ''} marked as paid
          </Typography>
          <Typography variant="body2">
            {formatDollars(batchResult.totalApplied)} recorded via {paymentTypeLabel}
          </Typography>
        </Alert>

        {/* Confirmation table */}
        <Paper sx={{ ...cardSx, mb: 2 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  {['Invoice #', 'Client', 'Horse', 'Service', 'Ref #'].map((h, i) => (
                    <TableCell
                      key={h}
                      align={i === 4 ? 'right' : 'left'}
                      sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {confirmedInvoices.map((inv) => {
                  const ref = refs[inv.id];
                  return (
                    <TableRow key={inv.id}>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 700 }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <CheckIcon sx={{ fontSize: '0.9rem', color: 'success.main' }} />
                          <span>{inv.invoiceNumber}</span>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{clientName(inv)}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{inv.horse?.name ?? '—'}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{primaryService(inv)}</TableCell>
                      <TableCell align="right">
                        {ref ? (
                          <Chip
                            label={ref}
                            size="small"
                            sx={{ bgcolor: '#fff8f3', border: '1px solid', borderColor: '#f5dcc8', color: '#8B4513', fontWeight: 700, fontSize: '0.7rem' }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.disabled" fontStyle="italic">none</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Payment meta */}
        <Paper sx={{ ...cardSx, mb: 2, p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 1.25 }}>
            Payment recorded
          </Typography>
          <Stack spacing={0.75}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Method</Typography>
              <Typography variant="body2">{paymentTypeLabel}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Date received</Typography>
              <Typography variant="body2">{dateDisplay}</Typography>
            </Stack>
            {batchNote && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Note</Typography>
                <Typography variant="body2">{batchNote}</Typography>
              </Stack>
            )}
            <Divider sx={{ my: 0.5 }} />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" fontWeight={700}>Total</Typography>
              <Typography variant="body2" fontWeight={700}>{formatDollars(batchResult.totalApplied)}</Typography>
            </Stack>
          </Stack>
        </Paper>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            sx={{ textTransform: 'none', borderColor: 'divider', color: 'text.secondary' }}
          >
            Export Summary
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            onClick={() => openDetail(confirmedInvoices[0].id)}
            sx={{ textTransform: 'none', borderColor: '#8B4513', color: '#8B4513', '&:hover': { bgcolor: 'rgba(139,69,19,0.04)' } }}
          >
            View Invoice Detail →
          </Button>
          <Button
            variant="contained"
            onClick={startOver}
            sx={{ bgcolor: '#3B2E1A', '&:hover': { bgcolor: '#2A2012' }, textTransform: 'none' }}
          >
            Done
          </Button>
        </Stack>
      </Box>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // SCREEN 3: Invoice Detail + editable ref #
  // ─────────────────────────────────────────────────────────────────────────────
  const renderScreen3 = () => {
    if (!detailInvoiceId) return null;
    const inv = confirmedInvoices.find((i) => i.id === detailInvoiceId);
    if (!inv) return null;
    const paymentId = batchResult?.results.find((r) => r.invoiceId === detailInvoiceId)?.paymentId;
    const refChanged = detailRef !== originalDetailRef;

    return (
      <Box sx={{ maxWidth: 560, mx: 'auto' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => setStep(1)}
          sx={{ textTransform: 'none', color: '#8B4513', mb: 1.5, pl: 0 }}
        >
          Back to confirmation
        </Button>

        <Stack direction="row" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h6" fontWeight={800}>{inv.invoiceNumber}</Typography>
            <Typography variant="caption" color="text.secondary">
              {clientName(inv)} · {inv.horse?.name ?? '—'}
            </Typography>
          </Box>
          <Chip
            icon={<CheckIcon sx={{ fontSize: '0.9rem !important' }} />}
            label="Paid"
            size="small"
            sx={{ ml: 'auto', bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700 }}
          />
        </Stack>

        {/* Invoice summary */}
        <Paper sx={{ ...cardSx, mb: 2 }}>
          <Box sx={{ px: 2, py: 1.25, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" fontWeight={700}>Invoice Summary</Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            <Stack spacing={1.25}>
              {[
                ['Client', clientName(inv)],
                ['Horse', inv.horse?.name ?? '—'],
                ['Service', primaryService(inv)],
                ['Invoice date', invoiceDate(inv)],
              ].map(([label, value]) => (
                <Stack key={label} direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">{label}</Typography>
                  <Typography variant="body2" fontWeight={600}>{value}</Typography>
                </Stack>
              ))}
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Amount</Typography>
                <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#8B4513' }}>
                  {formatDollars(invoiceBalance(inv))}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Paper>

        {/* Payment record */}
        <Paper sx={cardSx}>
          <Box sx={{ px: 2, py: 1.25, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" fontWeight={700}>Payment Record</Typography>
            <Typography variant="caption" sx={{ color: '#8B4513', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#8B4513', display: 'inline-block' }} />
              Ref # is editable
            </Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <Box flex={1}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 0.5 }}>
                    Method
                  </Typography>
                  <Box sx={{ border: '1.5px solid', borderColor: 'divider', borderRadius: 1, px: 1.25, py: 0.875, bgcolor: 'grey.50' }}>
                    <Typography variant="body2" color="text.secondary">{paymentTypeLabel}</Typography>
                  </Box>
                </Box>
                <Box flex={1}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 0.5 }}>
                    Date Received
                  </Typography>
                  <Box sx={{ border: '1.5px solid', borderColor: 'divider', borderRadius: 1, px: 1.25, py: 0.875, bgcolor: 'grey.50' }}>
                    <Typography variant="body2" color="text.secondary">{dateDisplay}</Typography>
                  </Box>
                </Box>
              </Stack>

              <Box>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 0.5 }}>
                  Reference Number{' '}
                  <Box component="span" sx={{ fontWeight: 400, color: 'text.disabled', textTransform: 'none', letterSpacing: 0 }}>
                    (wire ref, ACH trace, check #)
                  </Box>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter reference number…"
                  value={detailRef}
                  onChange={(e) => setDetailRef(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#8B4513', borderWidth: 1.5 },
                      bgcolor: '#fffbf8',
                    },
                  }}
                />
              </Box>

              {batchNote && (
                <Box>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 0.5 }}>
                    Batch Note
                  </Typography>
                  <Box sx={{ border: '1.5px solid', borderColor: 'divider', borderRadius: 1, px: 1.25, py: 0.875, bgcolor: 'grey.50' }}>
                    <Typography variant="body2" color="text.secondary">{batchNote}</Typography>
                  </Box>
                </Box>
              )}
            </Stack>
          </Box>

          <Box sx={{ px: 2, py: 1.25, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.disabled">
              {originalDetailRef ? `Ref: ${originalDetailRef}` : 'No reference number recorded'}
            </Typography>
            <Button
              variant="contained"
              size="small"
              disabled={!refChanged || refSaving || !paymentId}
              onClick={saveDetailRef}
              sx={{ bgcolor: '#8B4513', '&:hover': { bgcolor: '#7a3c10' }, textTransform: 'none', fontWeight: 600 }}
              startIcon={refSaving ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              Save Reference #
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Layout
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      {/* Back to billing */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/billing')}
        sx={{ textTransform: 'none', color: 'text.secondary', mb: 1.5, pl: 0 }}
      >
        Back to Invoicing
      </Button>

      {/* Stepper (hidden on screen 3) */}
      {step < 2 && (
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel
                sx={{
                  '& .MuiStepLabel-label': { fontWeight: 600 },
                  '& .MuiStepIcon-root.Mui-active': { color: '#8B4513' },
                  '& .MuiStepIcon-root.Mui-completed': { color: '#8B4513' },
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      {step === 0 && renderScreen1()}
      {step === 1 && renderScreen2()}
      {step === 2 && renderScreen3()}
    </Box>
  );
};

export default BulkMarkPaid;
